/**
 * Retry-cluster grouping for a story's agent runs (FORGE-224).
 *
 * A story's SESSION rows are a flat, interleaved log: `backend` may appear four
 * times (three force-completes, then a clean pass), `secops` six times, and an
 * abandoned `solo` approach six times before the pipeline split into
 * architect + specialists. Rendered as a flat table that reads as chaos.
 *
 * A force-complete is NORMAL process flow — the runtime kills a wandering agent
 * (no_progress / max_iterations / deadline / qa_budget) and relaunches it. What
 * matters is the *outcome per role*: did backend eventually pass, and after how
 * many attempts. This module folds the runs into one cluster per agent role and
 * classifies each role's terminal outcome.
 *
 * Pure + dependency-free so both the web app (rendering) and tests can use it.
 */
import type { EpicSession } from '../types/epics';

/** Per-attempt colour bucket for the amber→green attempt strip. */
export type AttemptTone = 'ok' | 'force' | 'fail' | 'running' | 'unknown';

/**
 * A role's terminal disposition across all its attempts:
 *  - `passed`      — one clean run, first try.
 *  - `passed_on`   — clean run after N force-completes (see `passedOnAttempt`).
 *  - `running`     — an attempt is currently in flight.
 *  - `abandoned`   — only ever force-completed, and a later role passed after it
 *                    stopped (a superseded approach, e.g. `solo` before a split).
 *  - `failing`     — only ever force-completed, nothing superseded it, not running
 *                    (genuinely stuck — the one that warrants attention).
 */
export type ClusterStatus = 'passed' | 'passed_on' | 'running' | 'abandoned' | 'failing';

export interface ClusterAttempt {
  tone: AttemptTone;
  sessionId: string;
  durationMs: number | null;
}

export interface RetryCluster {
  agentType: string;
  /** Raw runs, newest-first (for the expand-to-detail rows). */
  runs: EpicSession[];
  /** One entry per attempt, oldest→newest (for the strip). */
  attempts: ClusterAttempt[];
  attemptCount: number;
  status: ClusterStatus;
  /** 1-based attempt number that passed, when `status === 'passed_on'`. */
  passedOnAttempt: number | null;
  totalCostUsd: number;
  totalIterations: number;
  totalProductiveIterations: number;
  isRunning: boolean;
  firstStartedAt: string;
  lastStartedAt: string;
}

/** Classify one run into an attempt tone. Mirrors the epic page's outcomeTone. */
export function classifyAttempt(session: EpicSession): AttemptTone {
  if (session.isRunning) return 'running';
  const o = (session.outcome ?? '').toLowerCase();
  if (!o) return 'unknown';
  // Order matters: `force` is checked before the generic `complete` match so
  // `force_complete` never reads as a clean pass.
  if (o.includes('force') || o === 'terminated') return 'force';
  if (o.includes('complete') || o === 'task_complete') return 'ok';
  if (o.includes('fail') || o === 'error' || o === 'crashed') return 'fail';
  if (o.includes('rate') || o.includes('budget') || o.includes('progress') || o.includes('deadline')) {
    return 'force';
  }
  return 'unknown';
}

function startMs(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Group a single story's sessions into per-role retry clusters, ordered by the
 * pipeline progression (each role's earliest run ascending). Input order is
 * irrelevant — the function sorts internally.
 */
export function buildRetryClusters(sessions: EpicSession[]): RetryCluster[] {
  if (!sessions || sessions.length === 0) return [];

  // Timestamps of every clean pass — used to detect superseded (abandoned) roles.
  const okStartTimes: number[] = [];
  for (const s of sessions) {
    if (classifyAttempt(s) === 'ok') okStartTimes.push(startMs(s.startedAt));
  }

  const byRole = new Map<string, EpicSession[]>();
  for (const s of sessions) {
    const list = byRole.get(s.agentType) ?? [];
    list.push(s);
    byRole.set(s.agentType, list);
  }

  const clusters: RetryCluster[] = [];
  for (const [agentType, rawRuns] of byRole) {
    // Oldest→newest for attempt ordering; keep a newest-first copy for detail.
    const asc = [...rawRuns].sort((a, b) => startMs(a.startedAt) - startMs(b.startedAt));
    const desc = [...asc].reverse();

    const attempts: ClusterAttempt[] = asc.map((s) => ({
      tone: classifyAttempt(s),
      sessionId: s.sessionId,
      durationMs: s.durationMs,
    }));

    const isRunning = asc.some((s) => s.isRunning);
    const hasOk = attempts.some((a) => a.tone === 'ok');
    const firstStartedAt = asc[0]?.startedAt ?? '';
    const lastStartedAt = asc[asc.length - 1]?.startedAt ?? '';
    const lastMs = startMs(lastStartedAt);

    let status: ClusterStatus;
    let passedOnAttempt: number | null = null;
    if (isRunning) {
      status = 'running';
    } else if (hasOk) {
      // 1-based index of the (first) clean pass among ordered attempts.
      const idx = attempts.findIndex((a) => a.tone === 'ok');
      passedOnAttempt = idx + 1;
      status = passedOnAttempt === 1 ? 'passed' : 'passed_on';
    } else {
      // No clean run. Abandoned if some other role passed AFTER this one stopped.
      const superseded = okStartTimes.some((t) => t > lastMs);
      status = superseded ? 'abandoned' : 'failing';
    }

    clusters.push({
      agentType,
      runs: desc,
      attempts,
      attemptCount: asc.length,
      status,
      passedOnAttempt,
      totalCostUsd: asc.reduce((sum, s) => sum + (s.costUsd || 0), 0),
      totalIterations: asc.reduce((sum, s) => sum + (s.iterations || 0), 0),
      totalProductiveIterations: asc.reduce((sum, s) => sum + (s.productiveIterations || 0), 0),
      isRunning,
      firstStartedAt,
      lastStartedAt,
    });
  }

  // Pipeline order: by each role's earliest run. Running clusters float to the
  // bottom only if they genuinely started last (natural from the timestamp).
  clusters.sort((a, b) => startMs(a.firstStartedAt) - startMs(b.firstStartedAt));
  return clusters;
}
